import workIntervalApi from '@/data/api/workIntervals';

const local = {
  workIntervals: []
};

const workIntervalRepository = {
  /**
   * Seeds the in-memory mutation cache from the authoritative local read model.
   *
   * @param {Object[]} workIntervals - Intervals keyed by their local records.
   * @returns {Object[]} The seeded cache.
   */
  replaceCache: (workIntervals) => {
    local.workIntervals = [...workIntervals];
    return local.workIntervals;
  },

  /** @param {Object} interval - Persisted interval to merge into the cache. */
  upsertCachedWorkInterval: (interval) => {
    const index = local.workIntervals.findIndex(
      (item) => item.id === interval.id
    );
    if (index === -1) local.workIntervals.push(interval);
    else local.workIntervals[index] = interval;
    return interval;
  },

  /** @param {string|number} intervalId - Cached interval identifier to remove. */
  removeCachedWorkInterval: (intervalId) => {
    local.workIntervals = local.workIntervals.filter(
      (interval) => interval.id !== intervalId
    );
  },

  /**
   * Retrieves all work intervals from the local cache or fetches from the API if the cache is empty.
   * @returns {Promise<Array>} - A promise that resolves to an array of work intervals.
   */
  getAllWorkIntervals: () => {
    return workIntervalRepository
      .updateAllWorkIntervals()
      .then(() => local.workIntervals);
  },

  /**
   * Fetches all work intervals from the API and updates the local cache.
   * @returns {Promise<Array>} - A promise that resolves to an array of work intervals.
   */
  updateAllWorkIntervals: () => {
    return workIntervalApi
      .index()
      .then((response) => {
        local.workIntervals = response.data.payload || [];
        return local.workIntervals;
      })
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Creates a new work interval via the API and adds it to the local cache.
   * @param {Object} workIntervalData - The data for the new work interval.
   * @returns {Promise<Object>} - A promise that resolves to the new work interval object.
   */
  createWorkInterval: (workIntervalData) => {
    return workIntervalApi
      .create(workIntervalData)
      .then((response) => {
        const newWorkInterval = response.data.payload;
        local.workIntervals.push(newWorkInterval); // Add to local cache
        return newWorkInterval;
      })
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Updates an existing work interval via the API and updates the local cache.
   * @param {number|string} intervalId - The ID of the work interval to update.
   * @param {Object} updates - An object containing the fields to update.
   * @returns {Promise<Object>} - A promise that resolves to the updated work interval object.
   */
  updateWorkInterval: (intervalId, updates) => {
    return workIntervalApi
      .update(intervalId, updates)
      .then((response) => {
        const updatedWorkInterval = response.data.payload;
        // Update the local cache
        const index = local.workIntervals.findIndex(
          (wi) => wi.id === intervalId
        );
        if (index !== -1) {
          local.workIntervals[index] = updatedWorkInterval;
        }
        return updatedWorkInterval;
      })
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Deletes an existing work interval through the online API.
   *
   * The cache is updated only after the remote delete succeeds so a failed
   * request does not hide the interval from the current online flow.
   *
   * @param {number|string} intervalId - Work interval identifier.
   * @returns {Promise<void>} Resolves after the remote delete succeeds.
   */
  deleteWorkInterval: (intervalId) => {
    const index = local.workIntervals.findIndex((wi) => wi.id === intervalId);
    if (index === -1) {
      return Promise.reject(
        new Error(`Work interval with ID ${intervalId} not found`)
      );
    }

    return workIntervalApi.delete(intervalId).then(() => {
      local.workIntervals.splice(index, 1);
    });
  },

  /**
   * Retrieves a work interval by its ID from the local cache.
   * @param {number|string} intervalId - The ID of the work interval to retrieve.
   * @returns {Object|null} - The work interval object or null if not found.
   */
  getWorkIntervalById: (intervalId) => {
    return local.workIntervals.find((wi) => wi.id === intervalId) || null;
  }
};

export default workIntervalRepository;
